import { clsx } from "clsx"
import type { KeyboardEvent, MouseEvent, ReactNode } from "react"

import { type SX } from "../../theme"
import { ButtonGroup } from "../ButtonGroup"
import { Text, type TextProps } from "../Text"
import styles from "./Card.module.css"

export interface CardProps {
  children?: ReactNode
  color?: "grey1" | "grey2" | "grey3" | "indigo2" | "purple1"
  variant?: "dashed" | "elevated" | "outlined" | "selected"
  radius?: 4 | 6
  paddingBlock?: 0 | 8 | 16 | 28
  paddingInline?: 16 | 24
  onSelect?: (event: MouseEvent | KeyboardEvent) => void
  sx?: SX
}

export function Card(props: CardProps) {
  const {
    color = "grey1",
    variant = "elevated",
    radius = 6,
    paddingBlock = 8,
    paddingInline = 16,
    sx,
    children,
    onSelect
  } = props

  const interactive = Boolean(onSelect)

  function onKeyDown(event: KeyboardEvent) {
    if (event.code === "Enter" || event.code === "Space") onSelect?.(event)
  }

  function onClick(event: MouseEvent) {
    onSelect?.(event)
  }

  return (
    <div
      role={interactive ? "button" : "article"}
      tabIndex={interactive ? 0 : -1}
      className={clsx(
        styles.card,
        styles[`variant-${variant}`],
        styles[`color-${color}`],
        styles[`radius-${radius}`],
        styles[`paddingBlock-${paddingBlock}`],
        interactive && styles.interactive
      )}
      style={{ ...sx, "--Card-padding-inline": `${paddingInline}px` }}
      onKeyDown={onKeyDown}
      onClick={onClick}
    >
      {children}
    </div>
  )
}

interface CardHeaderProps {
  children: ReactNode
  sx?: SX
}

export function CardHeader(props: CardHeaderProps) {
  const { children, sx } = props

  return (
    <header className={styles.header} style={sx}>
      {children}
    </header>
  )
}

interface CardHeaderTitleProps {
  children?: ReactNode
  variant?: TextProps["variant"]
  sx?: SX
}

export function CardHeaderTitle(props: CardHeaderTitleProps) {
  const { children, variant, sx } = props

  return (
    <Text {...props} noSelect sx={sx} variant={variant}>
      {children}
    </Text>
  )
}

interface CardHeaderActionsProps {
  children?: ReactNode
  sx?: SX
}

export function CardHeaderActions(props: CardHeaderActionsProps) {
  const { sx, children } = props

  return (
    <ButtonGroup sx={sx} size="small" color="grey">
      {children}
    </ButtonGroup>
  )
}

interface CardContentProps {
  children?: ReactNode
  sx?: SX
}

export function CardContent(props: CardContentProps) {
  const { sx, children } = props

  return (
    <section className={styles.content} style={sx}>
      {children}
    </section>
  )
}

interface CardFooterProps {
  children?: ReactNode
  /**
   * Determines if the footer should have padding.
   * @default true
   */
  hasPadding?: boolean
  sx?: SX
}

export function CardFooter(props: CardFooterProps) {
  const { sx, hasPadding = true, children } = props

  return (
    <footer
      className={clsx(styles.footer, hasPadding && styles.footerPadding)}
      style={sx}
    >
      {children}
    </footer>
  )
}
